import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

interface IConditionRow {
	label: string;
	value: string;
	note?: string;
}

interface IMyTicketConditionsProps {
	/** Operator the ticket was purchased from ("Erworben bei"). */
	purchasedFrom: string;
	/**
	 * Whether the "Kontrollmedium" (Amtlicher Lichtbildausweis) row is shown.
	 * Only the yearly ticket is non-transferrable, so this note applies to it
	 * alone — hidden for daily / weekly / monthly / short tickets (MC-11405).
	 */
	showControlMedium: boolean;
	/**
	 * BE `discounted` flag: shows the "Kontrollmedium" row for any ticket type
	 * and adds the discount eligibility note as a second line.
	 */
	isDiscounted?: boolean;
}

/**
 * Static tariff meta-info block below the ticket card (Kontrollmedium,
 * Vertrieben durch, Erworben bei). Values not delivered by BE yet are sourced
 * from translations.
 */
export const MyTicketConditions: FC<IMyTicketConditionsProps> = ({
	purchasedFrom,
	showControlMedium,
	isDiscounted,
}) => {
	const { t } = useTranslation('ticketshop');

	const rows: IConditionRow[] = [
		...(showControlMedium || isDiscounted
			? [
					{
						label: t('my_ticket_detail.conditions.control_medium_label'),
						value: t('my_ticket_detail.conditions.control_medium_value'),
						note: isDiscounted
							? t('my_ticket_detail.conditions.control_medium_discount_value')
							: undefined,
					},
			  ]
			: []),
		{
			label: t('my_ticket_detail.conditions.distributed_by_label'),
			value: t('my_ticket_detail.conditions.distributed_by_value'),
		},
		{
			label: t('my_ticket_detail.conditions.purchased_from_label'),
			value: purchasedFrom,
		},
	];

	return (
		<Stack gap={2} sx={{ width: '100%' }}>
			{rows
				.filter(({ value }) => !!value)
				.map(({ label, value, note }) => (
					<Stack key={label} gap={0.5}>
						<Typography variant="l1" color="solutions.600">
							{label}
						</Typography>
						<Typography variant="b2" color="primary.main">
							{value}
						</Typography>
						{note && (
							<Typography variant="b2" color="primary.main">
								{note}
							</Typography>
						)}
					</Stack>
				))}
		</Stack>
	);
};
